import t from 'tcomb';
import { EventEmitter } from 'events';
import React from 'react';
import ReactDOM from 'react-dom';
import Server from './Server';
import buildHeightNotifier from '../domain/buildHeightNotifier';
import Locale from '../domain/Locale';
import UUID from '../domain/UUID';
import load_districts from '../domain/load_districts';
import target from '../domain/target';
import social_target from '../domain/social_target';
import setPurlCookies from '../domain/setPurlCookies';
import toUrl from '../domain/toUrl';
import cancel_target from '../domain/cancel_target';
import Share_Purchase from '../domain/SharePurchase';
import get_bundle from '../domain/get_bundle';
import GoogleAnalytics from '../domain/Google_Analytics';
import User from '../domain/User';
import Anonymous from '../domain/Anonymous';
import Registered from '../domain/Registered';
import isModal from '../domain/isModal';
import Alert from '../components/Alert';
import Confirm from '../components/Confirm';
import MarkdownComponent from '../components/MarkdownComponent';
import createCallback from '../../createCallback';
import Purchase from './route/Purchase';
import Purchases from './route/Purchases';
import ChangePassword from './route/ChangePassword';
import Account from './route/Account';
import Login from './route/Login';
import ResendPassword from './route/ResendPassword';
import UnexpectedError from './route/UnexpectedError';
import NoCookies from './route/NoCookies';
import Cookies from 'cookies-js';
import parseQuerystring from '../../parseQuerystring';
import template from 'lodash/string/template';

import jsdkTemplate from '../jsdkTemplate';
t.form.Form.templates = jsdkTemplate;

const on_start = function () {
  // comunica con il container quando l'applicazione è ospitata in una modale
  // e se il container supporta l'api postMessage()
  this.height_notifier = buildHeightNotifier(this);

  // ensure initial resize
  document.getElementsByTagName('body')[0].click = this.height_notifier;
  setInterval(this.height_notifier, 100);
};


// gestionde della privacy dei cookies
const doAcceptCookies = () => {
  Cookies.set('accept_cookies', true, {
    path: '/'
  });
  document.getElementById('cookies_disclaimer').style.display = 'none';
};

const getNavigateToFacebookLoginCommand = (app) => {
  const errorHandler = (err) => app.go_unexpected_err(err);
  const successHandler = (result) => {
    app.emit('go_social_login', ['facebook']);
    window.top.location.href = result.loginUri;
  };
  return () => app.server.loadFacebook({target: social_target(app), locale: app.locale}, createCallback(successHandler, errorHandler));
};

const getNavigateToGoogleLoginCommand = (app) => {
  const errorHandler = (err) => app.go_unexpected_err(err);
  const successHandler = (result) => {
    app.emit('go_social_login', ['google']);
    window.top.location.href = result.loginUri;
  };
  return () => app.server.loadGoogle({target: social_target(app), locale: app.locale}, createCallback(successHandler, errorHandler));
};

const getNavigateToTwitterLoginCommand = (app) => {
  const errorHandler = (err) => app.go_unexpected_err(err);
  const successHandler = (result) => {
    app.emit('go_social_login', ['twitter']);
    window.top.location.href = result.loginUri;
  };
  return () => app.server.loadTwitter({target: social_target(app), locale: app.locale}, createCallback(successHandler, errorHandler));
};

const getNavigateToMicrosoftLoginCommand = (app) => {
  const errorHandler = (err) => app.go_unexpected_err(err);
  const successHandler = (result) => {
    app.emit('go_social_login', ['microsoft']);
    window.top.location.href = result.loginUri;
  };
  return () => app.server.loadMicrosoft({target: social_target(app), locale: app.locale}, createCallback(successHandler, errorHandler));
};

const doLogout = (app) => {
  app.server.doLogout(function(err, user) {
    if (err) {
      app.go_unexpected_err(err);
    }
    else {
      app.user = user;
      app.transact(new LoginRoute());
    }
  });
};

function getInitialRoute(err, user) {
 if (err) {
    t.assert(t.maybe(t.Str).is(err));
    return new LoginRoute(<Alert type='error'>{err}></Alert>);
  }
  else {
    const params = parseQuerystring(location.search);
    if (params.purchase_id) {
      return new PurchaseRoute(params.purchase_id);
    }
    else if (Anonymous.is(user)) {
      return new LoginRoute();
    }
    else {
      return new PurchasesRoute();
    }
  }
}

export default class App extends EventEmitter {

  /* FIXME */
  Share_Purchase(){
    return Share_Purchase;
  }

  static go(mountNode, options) {
    setPurlCookies();
    const server = new Server(options.api_url);

    options.locale = Locale.of(options.locale);
    if (options.user && options.user.com_locale) {
      options.user.com_locale = Locale.of(options.user.com_locale);
    }
    options.user = User(options.user);

    options.route = getInitialRoute(options.err, options.user);

    const app = new App(mountNode, server, options);

    // track analytics infos
    const googleAnalytics = new GoogleAnalytics();

    function getPageview(name, args) {
      args = args || {};
      return toUrl(`user/${name}`, t.mixin({
        locale: app.locale.toJSON(),
        is_modal: isModal(),
        profile: Anonymous.is(app.user) ? 'anonymous' : Registered.is(app.user) ? 'registered' : 'verified'
      }, args));
    }

    // emitted when route changes
    app.on('route:change', function(route) {
      googleAnalytics.track_pageview(getPageview(route.name));
    });

    // emitted when a purchase is shared by the buyer
    app.on('do_share_purchase', function() {
      googleAnalytics.track_pageview(getPageview('do_share_purchase', {
        type: 'user'
      }));
    });

    // emitted when the user try to login with socials
    app.on('go_social_login', function(args) {
      const type = args[0];
      googleAnalytics.track_pageview(getPageview('go_social_login', { type }));
    });

    app.transact(options.route);

    on_start.bind(app)();

    return app;
  };

  constructor(mountNode, server, options) {
    super();
    t.assert(t.Str.is(options.lib_url));
    t.assert(Locale.is(options.locale));
    t.assert(User.is(options.user));
    t.assert(t.Obj.is(options.route));
    t.assert(t.Str.is(options.share_purchase_url));

    t.mixin(this, options);
    this.mountNode = mountNode;
    this.server = server;
    this.height_notifier = buildHeightNotifier(this);
  }

  // da chiamare quando si riceve una access denied da una chiamata alle api sotto autenticazione
  go_expired_session(locale) {
    const bundle = get_bundle(locale);
    this.user = User({ profile: 'anonymous' });
    this.transact(new LoginRoute(<Alert type='error'>{bundle['template_message']['login.access denied']}</Alert>));
  }

  go_unexpected_err(err) {
    this.transact(new UnexpectedErrorRoute(err));
  }

  stop() {
    ReactDOM.unmountComponentAtNode(this.mountNode);
  }

  transact(route) {
    this.stop();
    this.route = route;
    route.to_widget(this, (widget) => {
      ReactDOM.render(widget, this.mountNode);
      this.emit('route:change', route);
      this.height_notifier();
      window.scrollTo(0, 0);
    });
  }
}

//
// routes
//
class UnexpectedErrorRoute {

  constructor(err) {
    this.name = 'unexpected_error';
    this.err = err;
  }

  to_widget(app, callback) {

    const doRefresh = () => {
      location.reload();
    };

    callback(<UnexpectedError
            locale={app.locale}
            err={this.err}
            doAcceptCookies={doAcceptCookies}
            doRefresh={doRefresh} />);
  }
}

class ResendPasswordRoute {

  constructor(message) {
    this.name = 'resend_password';
    this.message = message;
  }

  to_widget(app, callback) {
    const locale = app.locale;
    const bundle = get_bundle(locale);

    const doResendPassword = (email) => {
      app.server.doResendPassword({email}, function(err) {
        if (err) {
          switch (err.message) {
            case 'invalid emailAddress':
              const msg = template(bundle['template_message']['invalid emailAddress'])({
                email: email
              });
              app.transact(new ResendPasswordRoute(<Alert type='error'>{msg}</Alert>));
              break;
            default:
              app.go_unexpected_err(err);
          }
        }
        else {
          const msg = template(bundle['template_message']['resend_password.done'])({
            email: email
          });
          app.transact(new LoginRoute(<Alert type='success'><MarkdownComponent>{msg}</MarkdownComponent></Alert>));
        }
      });
    };

    const go_login = () => {
      app.transact(new LoginRoute());
    };

    callback(<ResendPassword
      locale={locale}
      message={this.message}
      doResendPassword={doResendPassword}
      goLogin={go_login}
      doAcceptCookies={doAcceptCookies}/>);
  }

}

class LoginRoute {

  constructor(message) {
    this.name = 'login';
    this.message = message;
  }

  to_widget(app, callback) {
    const locale = app.locale;
    const bundle = get_bundle(locale)['template_message'];

    const goResendPassword = () => {
      app.transact(new ResendPasswordRoute());
    };

    const doLogin = (email, password) => {
      app.server.doLogin({email, password}, function(err, user) {
        if (err) {
          switch (err.message) {
            case 'account deleted':
            case 'invalid emailAddress':
              const msg = template(bundle[err.message])({
                email: email
              });
              app.transact(new LoginRoute(<Alert type='error'>{msg}</Alert>));
              break;
            case 'access denied':
              app.transact(new LoginRoute(<Alert type='error'>{bundle['login.access denied']}</Alert>));
              break;
            default:
              app.go_unexpected_err(err);
          }
        }
        else {
          user.com_locale = Locale.of(user.com_locale);
          app.user = User(user);
          app.transact(new PurchasesRoute());
        }
      });
    };

    callback(<Login
        locale={locale}
        message={this.message}
        goResendPassword={goResendPassword}
        doLogin={doLogin}
        goFacebookLogin={getNavigateToFacebookLoginCommand(app)}
        goGoogleLogin={getNavigateToGoogleLoginCommand(app)}
        goTwitterLogin={getNavigateToTwitterLoginCommand(app)}
        goMicrosoftLogin={getNavigateToMicrosoftLoginCommand(app)}
        doAcceptCookies={doAcceptCookies}/>);

  }

}

class ChangePasswordRoute {

  constructor(message) {
    this.name = 'change_password';
    this.message = message;
  }

  to_widget(app, callback) {
    const locale = app.locale;

    const goAccount = () => {
      app.transact(new AccountRoute());
    };

    const goPurchases = () => {
      app.transact(new PurchasesRoute());
    };

    const goChangePassword = () => {
      app.transact(new ChangePasswordRoute());
    };

    const doChangePassword = (old_password, new_password) => {
      const bundle = get_bundle(locale)['template_message'];
      app.server.doChangePassword({old_password, new_password}, function(err) {
        if (err) {
          switch (err.message) {
            case 'access denied':
              app.go_expired_session();
              break;
            case 'invalid old password':
            case 'invalid new password':
              app.transact(new ChangePasswordRoute(<Alert type='error'>{bundle['change_password.' + err.message]}</Alert>));
              break;
            default:
              app.go_unexpected_err(err);
          }
        }
        else {
          app.transact(new ChangePasswordRoute(<Alert type='success'>{bundle['change_password.done']}</Alert>));
        }
      });
    };

    const _doLogout = () => {
      doLogout(app);
    };

    callback(<ChangePassword user={app.user}
                locale={locale}
                message= {this.message}
                doChangePassword={doChangePassword}
                goAccount={goAccount}
                goChangePassword={goChangePassword}
                goPurchases={goPurchases}
                doLogout={_doLogout}
                doAcceptCookies={doAcceptCookies}/>);
  }

}

class AccountRoute {

  constructor(message) {
    this.name = 'account';
    this.message = message;
  }

  to_widget(app, callback) {
    const { locale, user, lib_url } = app;
    const bundle = get_bundle(locale);

    load_districts(lib_url, user.country, (districts) => {

      const load_districts_bck = load_districts;   //TODO ERRORE

      const _load_districts = (country, callback) => {
        load_districts(lib_url, country, callback);
      };

      const goAccount = () => {
        app.transact(new AccountRoute());
      };

      const goPurchases = () => {
        app.transact(new PurchasesRoute());
      };

      const goChangePassword = () => {
        app.transact(new ChangePasswordRoute());
      };

      const doUpdateAccount = (name, surname, gender, birth_date, country, district, zip, com_locale) => {

        app.server.doUpdateAccount({name, surname, gender, birth_date, country, district, zip, com_locale: Locale.of(com_locale)}, (err, user) => {
            if (err) {
              switch (err.message) {
                case 'access denied':
                  app.go_expired_session();
                  break;
                default:
                  app.go_unexpected_err(err);
              }
            }
            else {
              load_districts_bck(lib_url, country, function() {
                user.com_locale = Locale.of(user.com_locale);
                app.user = User(user);
                app.transact(new AccountRoute(<Alert type='success'>{bundle['template_message']['account.done']}</Alert>));
              });
            }
          });
      };

      const _doLogout = () => {
        doLogout(app);
      };

      callback(<Account
                  locale={locale}
                  user={user}
                  districts={districts}
                  loadDistricts={_load_districts}
                  locale={locale}
                  message={this.message}
                  doUpdateAccount={doUpdateAccount}
                  goAccount={goAccount}
                  goChangePassword={goChangePassword}
                  goPurchases={goPurchases}
                  doLogout={_doLogout}
                  doAcceptCookies={doAcceptCookies}/>);
    });
  }

}

class PurchasesRoute {

  constructor() {
    this.name = 'purchases';
  }

  to_widget(app, callback) {
    app.server.loadPurchases(function(err, purchases_raw) {
      if (err) {
         switch (err.message) {
          case 'access denied':
            app.go_expired_session(app.locale);
            break;
          default:
            app.go_unexpected_err(err);
        }
      }
      else {
        const goPurchase = (id) => {
          app.transact(new PurchaseRoute(id));
        };

        const goAccount = () => {
          app.transact(new AccountRoute());
        };

        const goPurchases = () => {
          app.transact(new PurchasesRoute());
        };

        const goChangePassword = () => {
          app.transact(new ChangePasswordRoute());
        };

        const _doLogout = () => {
          doLogout(app);
        };
        callback(<Purchases
                    user={app.user}
                    locale={app.locale}
                    purchases={purchases_raw}
                    goPurchase={goPurchase}
                    goAccount={goAccount}
                    goChangePassword={goChangePassword}
                    goPurchases={goPurchases}
                    doLogout={_doLogout}
                    doAcceptCookies={doAcceptCookies}/>);
      }
    });
  }

}

class PurchaseRoute {

  constructor(purchase_id, message) {
    t.assert(UUID.is(purchase_id));
    this.name = 'purchase';
    this.purchase_id = purchase_id;
    this.message = message;
  }

  to_widget(app, callback) {
    const locale = app.locale;
    const purchase_id = this.purchase_id;

    const goAccount = () => {
      app.transact(new AccountRoute());
    };

    const goPurchases = () => {
      app.transact(new PurchasesRoute());
    };

    const goChangePassword = () => {
      app.transact(new ChangePasswordRoute());
    };


    app.server.loadPurchase({purchase_id, target: target(app, purchase_id), cancel_target: cancel_target(app)}, (err, purchase) => {
      if (err) {
        switch (err.message) {
          case 'access denied':
            app.go_expired_session();
            break;
          default:
            app.go_unexpected_err(err);
        }
      }
      else {

        Share_Purchase.create(app.share_purchase_url, app.locale, purchase, function() {
          return true;
        });

        const do_send_gift_card_code = (gift_card_code) => {
          const bundle = get_bundle(locale);
          const cbk = (b) => {
            if (b) {
              app.server.doSendGiftCardCode({purchase_id, target: target(app), cancel_target: cancel_target(app, purchase_id), gift_card_code}, function(err) {
                if (err) {
                  switch (err.message) {
                    case 'access denied':
                      app.go_expired_session();
                      break;
                    case 'gift card error':
                      app.transact(new PurchaseRoute(purchase_id, <Alert type='error'>{bundle['template_message']['purchase.gift card error']}</Alert>));
                      break;
                    default:
                      app.go_unexpected_err(err);
                  }
                }
                else {
                  app.transact(new PurchaseRoute(purchase_id));
                }
              });
            } else {
              ReactDOM.unmountComponentAtNode(document.getElementById('modal-container'));
            }
          };
          ReactDOM.render(<Confirm callback={cbk} locale={locale} width='600' title={bundle.order.gift_card.confirm_title}><Alert type='warning'><MarkdownComponent>{bundle.order.gift_card.confirm_body}</MarkdownComponent></Alert></Confirm>, document.getElementById('modal-container'));
        };

        const _doLogout = () => {
          doLogout(app);
        };

        callback(<Purchase
            locale={locale}
            message={this.message}
            purchase={purchase}
            user={app.user}
            share_purchase={Share_Purchase.current.html()}
            custom_payments={purchase.custom_payments || []}
            pending_purchase_message={purchase.pending_purchase_message}
            doSendGiftCardCode={do_send_gift_card_code}
            goAccount={goAccount}
            goChangePassword={goChangePassword}
            goPurchases={goPurchases}
            doLogout={_doLogout}
            doAcceptCookies={doAcceptCookies}/>);
      }
    });
  }

}

class NoCookiesRoute {

  constructor() {
    this.name = 'no_cookies';
  }

  to_widget(app, callback) {
    callback(<NoCookies locale={app.locale} />);
  }

}




/** WEBPACK FOOTER **
 ** C:/repository/madai/v6/client/lib/web/user/app.js
 **/